import { Info as InfoIcon, Plus, Trash2 } from 'lucide-react';
import { Fragment, useId, useState, type ReactNode } from 'react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { Input } from '@voll-intelligence/ui-kit/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@voll-intelligence/ui-kit/ui/popover';
import { FIELD_CONTROL_SIZE, FieldShell } from '../../shared/field/field_shell.js';
import type { AllocationCatalog, AllocationDraft } from './allocation.js';
import {
  AllocationField,
  type AllocationFieldCopy,
  type CostCenterSearch,
} from './allocation_field.js';
import {
  MAX_ALLOCATION_ROWS,
  addAllocationRow,
  allocationSplitProblem,
  parsePercentInput,
  removeAllocationRow,
  withPercent,
  type AllocationSplitProblem,
} from './allocation_split.js';

export interface AllocationSplitCopy {
  readonly percent: string;
  readonly add: string;
  /** Nome acessível da lixeira de uma linha. */
  readonly remove: string;
  /** Nome acessível de cada linha: "Centro de custo 2". */
  readonly row: (position: number) => string;
  /** "Qual será o fluxo de aprovação?" e as frases do legado que o explicam. */
  readonly approvalFlow: string;
  readonly approvalFlowPhrases: readonly string[];
  readonly problems: Readonly<Record<AllocationSplitProblem, string>>;
}

export interface AllocationListFieldProps {
  catalog: AllocationCatalog;
  value: readonly AllocationDraft[];
  onChange: (rows: AllocationDraft[]) => void;
  /** A flag `allow_many_allocations` de quem reserva. Sem ela o campo é o
   *  `AllocationField` de uma linha, como antes do rateio. */
  allowMany: boolean;
  searchCostCenters?: CostCenterSearch | undefined;
  copy: AllocationFieldCopy;
  splitCopy: AllocationSplitCopy;
  projectRequired?: boolean | undefined;
  /** O aviso de orçamento de cada linha, na mesma ordem das linhas (só com duas
   *  linhas ou mais; com uma, o host o mostra onde sempre mostrou). */
  rowFooters?: readonly ReactNode[] | undefined;
}

/** O percentual de uma linha. Guarda o texto digitado ("33," no meio da
 *  digitação) e só devolve o número; um valor novo vindo de fora (a divisão ao
 *  adicionar ou remover linha) substitui o texto. */
function PercentField({
  label,
  value,
  onChange,
}: {
  readonly label: string;
  readonly value: number | undefined;
  readonly onChange: (value: number | undefined) => void;
}) {
  const id = useId();
  const [text, setText] = useState(value === undefined ? '' : String(value));
  // Um valor novo de fora reescreve o texto durante o render; o que já diz o mesmo número fica.
  const [textFor, setTextFor] = useState(value);
  if (textFor !== value) {
    setTextFor(value);
    if (parsePercentInput(text)?.value !== value) setText(value === undefined ? '' : String(value));
  }
  return (
    <FieldShell label={label} fieldId={id}>
      <div className="relative">
        <Input
          id={id}
          inputMode="decimal"
          className={`${FIELD_CONTROL_SIZE} pr-7`}
          value={text.replace('.', ',')}
          onChange={(event) => {
            const parsed = parsePercentInput(event.target.value);
            if (!parsed) return;
            setText(parsed.text);
            onChange(parsed.value);
          }}
        />
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-sm text-muted-foreground"
        >
          %
        </span>
      </div>
    </FieldShell>
  );
}

/** "Qual será o fluxo de aprovação?": a explicação do legado, num popover que
 *  abre no toque também. */
function ApprovalFlowHint({ copy }: { readonly copy: AllocationSplitCopy }) {
  return (
    <Popover>
      <PopoverTrigger
        render={
          <Button type="button" variant="link" size="sm" className="px-0">
            <InfoIcon aria-hidden />
            {copy.approvalFlow}
          </Button>
        }
      />
      <PopoverContent align="start" className="flex w-80 flex-col gap-2 text-sm">
        {copy.approvalFlowPhrases.map((phrase) => (
          <p key={phrase} className="m-0">
            {phrase}
          </p>
        ))}
      </PopoverContent>
    </Popover>
  );
}

function AllocationRow({
  props,
  row,
  index,
}: {
  readonly props: AllocationListFieldProps;
  readonly row: AllocationDraft;
  readonly index: number;
}) {
  const { value, onChange, splitCopy } = props;
  const update = (next: AllocationDraft) => {
    onChange(value.map((current, i) => (i === index ? next : current)));
  };
  return (
    <div role="group" aria-label={splitCopy.row(index + 1)} className="flex flex-col gap-3">
      <AllocationField
        catalog={props.catalog}
        value={row}
        searchCostCenters={props.searchCostCenters}
        copy={props.copy}
        projectRequired={props.projectRequired}
        editing
        onChange={(next) => {
          // A troca de empresa devolve um objeto novo sem o percentual: ele é
          // da linha, não da escolha.
          update(withPercent(next, row.percent));
        }}
      />
      <div className="flex items-end gap-2">
        <div className="w-32">
          <PercentField
            label={splitCopy.percent}
            value={row.percent}
            onChange={(percent) => {
              update(withPercent(row, percent));
            }}
          />
        </div>
        {index > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="icon-lg"
            aria-label={splitCopy.remove}
            className="text-destructive"
            onClick={() => {
              onChange(removeAllocationRow(value, index));
            }}
          >
            <Trash2 aria-hidden />
          </Button>
        )}
      </div>
      {props.rowFooters?.[index]}
    </div>
  );
}

/**
 * O rateio de centro de custo do checkout (#6697): até `MAX_ALLOCATION_ROWS`
 * linhas de empresa, centro de custo e projeto, cada uma com o seu percentual.
 * Comportamento do legado (`AllocationFieldListView`): o percentual só aparece
 * com duas linhas ou mais, a 1ª linha não sai, e adicionar ou remover divide
 * 100% em partes iguais (a sobra na 1ª, para a soma sempre fechar).
 *
 * Só avisa o que não fecha (soma, linha sem percentual, centro de custo
 * repetido); quem trava o envio é a regra do host.
 */
export function AllocationListField(props: Readonly<AllocationListFieldProps>) {
  const { value, onChange, allowMany, splitCopy } = props;
  const problemId = useId();
  // Uma linha: o campo de sempre, sem percentual (ela vale 100%).
  const singleRow = value.length <= 1 && (
    <AllocationField
      catalog={props.catalog}
      value={value[0] ?? { companyId: '', costCenterId: '', projectId: '' }}
      searchCostCenters={props.searchCostCenters}
      copy={props.copy}
      projectRequired={props.projectRequired}
      editing
      onChange={(next) => {
        onChange([next]);
      }}
    />
  );
  if (!allowMany && singleRow) return singleRow;
  const problem = allocationSplitProblem(value);
  return (
    <div className="flex flex-col gap-3" aria-describedby={problem ? problemId : undefined}>
      {singleRow ||
        value.map((row, index) => (
          // eslint-disable-next-line react/no-array-index-key -- the row has no id of its own: it IS its position (the percent and the trash belong to it)
          <Fragment key={index}>
            {index > 0 && <hr className="m-0 border-border" />}
            <AllocationRow props={props} row={row} index={index} />
          </Fragment>
        ))}
      {problem && (
        <p id={problemId} role="alert" className="m-0 text-xs font-medium text-destructive">
          {splitCopy.problems[problem]}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        {value.length < MAX_ALLOCATION_ROWS && (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="px-0"
            onClick={() => {
              onChange(addAllocationRow(value));
            }}
          >
            <Plus aria-hidden />
            {splitCopy.add}
          </Button>
        )}
        <ApprovalFlowHint copy={splitCopy} />
      </div>
    </div>
  );
}
